import React from "react";

import { css, cx } from "@emotion/css";
import { Group } from "@visx/group";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { SvgLegendItem } from "@/uiKit/atoms/svg/SvgLegendItem.tsx";
import { SvgLayout } from "@/utils/graphUtils.ts";

export type BaseLegendProps = {
  enabled: boolean;
  legendContentWidth: number;
  className?: string;
};

export type DiscreteInsulinLegendProps = BaseLegendProps & {
  fastLegend: SvgLayout;
  slowLegend: SvgLayout;
};

const DiscreteInsulinLegend_: React.FC<DiscreteInsulinLegendProps> = ({
  enabled,
  legendContentWidth,
  fastLegend,
  slowLegend,
  className,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  if (!enabled) {
    return null;
  }
  return (
    <Group className={cx(styles.container, className)}>
      <Group {...fastLegend}>
        <SvgLegendItem
          label={t("charts.insulin.fast", {
            context: legendContentWidth < 80 ? "short" : "",
          })}
          unit={t("charts.insulin.fastUnit")}
        />
      </Group>
      <Group {...slowLegend}>
        <SvgLegendItem
          label={t("charts.insulin.slow", {
            context: legendContentWidth < 80 ? "short" : "",
          })}
          unit={t("charts.insulin.slowUnit")}
        />
      </Group>
    </Group>
  );
};

export const DiscreteInsulinLegend = React.memo(DiscreteInsulinLegend_);

export type ContinuousInsulinLegendProps = BaseLegendProps & {
  bolusLegend: SvgLayout;
  basalLegend: SvgLayout;
  closedLoopLegend: SvgLayout;
  hasClosedLoopData: boolean;
};

const ContinuousInsulinLegend_: React.FC<ContinuousInsulinLegendProps> = ({
  enabled,
  legendContentWidth,
  bolusLegend,
  basalLegend,
  closedLoopLegend,
  hasClosedLoopData,
  className,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  if (!enabled) {
    return null;
  }
  return (
    <Group className={cx(styles.container, className)}>
      <Group {...bolusLegend}>
        <>
          <SvgLegendItem
            label={t("charts.insulin.bolus")}
            unit={legendContentWidth < 60 ? "" : t("charts.insulin.bolusUnit")}
          />
          {legendContentWidth >= 60 ? (
            <SvgLegendItem
              top={20}
              label={`& ${t("charts.insulin.carbs", {
                context: legendContentWidth < 90 ? "short" : "",
              })}`}
              unit={t("charts.insulin.carbsUnit")}
            />
          ) : null}
        </>
      </Group>
      <Group {...basalLegend}>
        <SvgLegendItem
          label={t("charts.insulin.basalRate", {
            context: legendContentWidth < 90 ? "short" : "",
          })}
          unit={legendContentWidth < 60 ? "" : t("charts.insulin.basalRateUnit")}
        />
      </Group>
      {hasClosedLoopData && (
        <Group {...closedLoopLegend}>
          <SvgLegendItem label={t("charts.insulin.closedLoop")} />
        </Group>
      )}
    </Group>
  );
};

export const ContinuousInsulinLegend = React.memo(ContinuousInsulinLegend_);

const makeStyles = () => ({
  container: css``,
});
